import type { NavGroup, SidebarView } from './layout/types';

/**
 * Aplatizarea navigaţiei pentru paleta de comenzi.
 *
 * Stă separat de componentă pentru că e singura parte cu reguli — dedublare, prefixare,
 * text de căutat — iar regulile astea trebuie testate fără să randezi un dialog.
 *
 * Ce vede paleta: **grupurile rădăcină şi vederile drill-down**. Până la #76 citea doar
 * grupurile, deci tot ce stă sub `/setari` era de negăsit — nu pentru că ar fi fost
 * ascuns, ci pentru că structura care le ţine (`views`) nu ajungea până aici.
 *
 * ⚠️ Ce **nu** face: nu filtrează pe permisiuni. Filtrarea s-a făcut deja, în aplicaţie,
 * peste aceleaşi grupuri şi vederi pe care le primeşte sidebar-ul (`useAppShell`). Dacă
 * cineva cheamă funcţia asta cu date nefiltrate, paleta devine o portiţă către ecrane pe
 * care meniul le ascunde.
 */

export type CommandEntry = {
  key: string;
  /** Ce se citeşte pe rând: „Setări › Pachete". */
  label: string;
  /** Ce potriveşte `cmdk`: eticheta, varianta fără diacritice şi url-ul. */
  value: string;
  url: string;
  shortcut?: string[];
};

export type CommandSection = {
  key: string;
  heading: string;
  entries: CommandEntry[];
};

/**
 * „Setări" → „setari".
 *
 * Fără asta, „setari" tastat de la o tastatură fără diacritice n-ar găsi „Setări".
 * Url-urile sunt oricum fără diacritice, deci ar fi mers şi pe ele — dar numai din
 * noroc: prima pagină al cărei titlu nu se regăseşte în rută ar fi rupt regula tăcut.
 */
function fold(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function makeEntry(
  keyPrefix: string,
  labelPath: string[],
  url: string,
  shortcut: string[] | undefined,
): CommandEntry {
  const label = labelPath.join(' › ');
  // Toate segmentele intră în text, nu doar frunza: „setari" trebuie să găsească tot ce e
  // în zonă, iar „jurnal" să găsească „Setări › Jurnal de activitate".
  const searchable = `${label} ${url}`;

  return {
    key: `${keyPrefix}:${url}`,
    label,
    value: `${searchable} ${fold(searchable)}`,
    url,
    shortcut,
  };
}

function entriesFromGroups(
  groups: NavGroup[],
  keyPrefix: string,
  parents: string[],
): CommandEntry[] {
  return groups.flatMap((group, groupIndex) =>
    group.items.flatMap((item) => {
      // Acelaşi discriminant ca în `nav-group.tsx`: `items` separă cele două forme.
      // Îngustarea pe `url` nu funcţionează — tipul lui din TanStack Router include deja
      // `undefined`, deci un test de adevăr nu spune din ce ramură a uniunii venim.
      if (!item.items) {
        return [
          makeEntry(
            `${keyPrefix}:${groupIndex}`,
            [...parents, item.title],
            item.url as string,
            item.shortcut,
          ),
        ];
      }

      return item.items.map((subItem) =>
        makeEntry(
          `${keyPrefix}:${groupIndex}`,
          [...parents, item.title, subItem.title],
          subItem.url as string,
          subItem.shortcut,
        ),
      );
    }),
  );
}

/**
 * Un url, un rând.
 *
 * „Setări" din meniul rădăcină şi „Setările aplicaţiei", prima intrare a vederii, duc
 * amândouă la `/setari`. Se păstrează **cea cu scurtătură** — altfel operatorul vede două
 * rânduri identice ca efect, iar cel care anunţă `G S` nu e neapărat cel de deasupra.
 * Alternativa (redenumiri în `sidebar-data`) mută problema, n-o rezolvă: următoarea vedere
 * drill-down o readuce.
 */
function dedupeByUrl(sections: CommandSection[]): CommandSection[] {
  const kept = new Map<string, CommandEntry>();

  for (const section of sections) {
    for (const entry of section.entries) {
      const existing = kept.get(entry.url);

      if (!existing || (!existing.shortcut?.length && entry.shortcut?.length)) {
        kept.set(entry.url, entry);
      }
    }
  }

  return sections
    .map((section) => ({
      ...section,
      entries: section.entries.filter((entry) => kept.get(entry.url) === entry),
    }))
    .filter((section) => section.entries.length > 0);
}

export function buildCommandSections(
  groups: NavGroup[],
  views: SidebarView[] = [],
): CommandSection[] {
  const rootSections: CommandSection[] = groups.map((group, index) => ({
    key: `group:${index}`,
    heading: group.title || 'Navigare',
    entries: entriesFromGroups([group], `group:${index}`, []),
  }));

  // Fiecare vedere e o secţiune proprie, cu titlul ei ca antet — şi acelaşi titlu prefixat
  // pe fiecare rând, ca rândul să spună unde duce şi când lista e filtrată la un singur
  // rezultat, fără antet vizibil deasupra.
  const viewSections: CommandSection[] = views.map((view) => ({
    key: `view:${view.id}`,
    heading: view.title,
    entries: entriesFromGroups(view.navGroups, `view:${view.id}`, [view.title]),
  }));

  return dedupeByUrl([...rootSections, ...viewSections]);
}

export function flattenCommandEntries(sections: CommandSection[]): CommandEntry[] {
  return sections.flatMap((section) => section.entries);
}
